import { useCallback, useMemo, useState } from 'react';
import { ColumnSort, SortingState } from '@tanstack/react-table';

/**
 * Hook for managing table sorting state
 */
export const useSorting = (props: ColumnSort[] = []) => {
  const [state, setState] = useState<SortingState>(props);

  const sortParams = useMemo<
    | {
        sort_column: string;
        sort_direction: 'asc' | 'desc';
      }
    | undefined
  >(() => {
    if (state.length === 0) {
      return undefined;
    }

    const firstSort = state[0];
    return {
      sort_column: firstSort.id,
      sort_direction: (firstSort.desc ? 'desc' : 'asc') as 'asc' | 'desc',
    };
  }, [state]);

  return { state, onChange: setState, sortParams };
};

/**
 * Props for usePagination hook
 */
type UsePaginationProps = {
  page: number;
  size_per_page: number;
};

/**
 * Hook for managing table pagination state
 */
export const usePagination = (props: UsePaginationProps) => {
  const [{ page, size_per_page }, setPagination] = useState(props);

  const onPageChange = useCallback(
    (page: number) => {
      setPagination({ page, size_per_page });
    },
    [size_per_page],
  );

  const onSizePerPageChange = useCallback((sizePerPage: number) => {
    setPagination({ page: 1, size_per_page: sizePerPage });
  }, []);

  return {
    page,
    size_per_page,
    onPageChange,
    onSizePerPageChange,
  };
};

/**
 * Props for useTablePagination hook
 */
type UseTablePaginationProps = {
  columnSort?: ColumnSort[];
  pagination: UsePaginationProps;
};

/**
 * Hook for managing table pagination and sorting
 * Combines usePagination and useSorting hooks
 */
export const useTablePagination = (props: UseTablePaginationProps) => {
  const { columnSort = [], pagination: defaultPagination } = props;
  const sorting = useSorting(columnSort);
  const pagination = usePagination(defaultPagination);

  return { sorting, pagination };
};
